import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Send, Clock, Paperclip, X, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { TicketAttachment } from '@/components/tickets/TicketAttachment';
import { ticketSenderLabel } from '@/components/tickets/senderLabel';
import { format } from 'date-fns';
import { apiErrorMessage } from '@/lib/api-error';

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

type TicketResource = components['schemas']['TicketResource'];

const priorityLabel = (p: string) => i18n.t(`tick_common.priority.${p}`);
const priorityColors: Record<string, string> = {
  low: 'bg-muted text-muted-foreground',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  high: 'bg-destructive/10 text-destructive',
};
const statusLabel = (s: string) => i18n.t(`tick_common.status.${s}`);
const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  reviewing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  closed: 'bg-muted text-muted-foreground',
};

const BUILTIN_MODULE_CODES = ['hisob_app', 'customers', 'murabaha', 'inventory', 'products', 'sales', 'pricing', 'branches', 'reports', 'pos'];
const builtinModules = (): Record<string, string> =>
  Object.fromEntries(BUILTIN_MODULE_CODES.map((c) => [c, i18n.t(`tick_common.modules.${c}`)]));

interface Props {
  ticketId: number;
  onBack: () => void;
}

export default function TicketDetail({ ticketId, onBack }: Props) {
  const { t } = useTranslation();
  const BUILTIN_MODULES = builtinModules();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('tick_detail.fileTooBig'));
      return;
    }
    setSelectedFile(file);
  };

  const { data: ticketData, isLoading } = useQuery({
    queryKey: ['/tickets/{ticket}', ticketId],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/tickets/{ticket}', {
        params: { path: { ticket: ticketId } },
      });
      if (error) throw error;
      return data.data as TicketResource;
    },
    refetchInterval: 10_000,
  });
  const ticket = ticketData;

  const sendMutation = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      if (message.trim()) body.append('message', message.trim());
      if (selectedFile) body.append('attachment', selectedFile);
      const { error } = await apiClient.POST('/tickets/{ticket}/messages', {
        params: { path: { ticket: ticketId } },
        body: body as unknown as never,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/tickets/{ticket}', ticketId] });
      setMessage('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (e) => toast.error(apiErrorMessage(e, t('tick_detail.sendFailed'))),
  });

  const statusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const { error } = await apiClient.PATCH('/tickets/{ticket}', {
        params: { path: { ticket: ticketId } },
        body: { status: newStatus as 'new' | 'reviewing' | 'resolved' | 'closed' },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/tickets/{ticket}', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['/tickets'] });
      toast.success(t('tick_detail.statusUpdated'));
    },
    onError: (e) => toast.error(apiErrorMessage(e, t('tick_detail.error'))),
  });

  const isOwnerOrManager = user?.role === 'owner' || user?.role === 'manager';

  const messageCount = ticket?.messages?.length ?? 0;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messageCount]);

  if (isLoading) return <div className="p-6 text-muted-foreground">{t('tick_detail.loading')}</div>;
  if (!ticket) return <div className="p-6 text-muted-foreground">{t('tick_detail.notFound')}</div>;

  const messages = ticket.messages ?? [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft size={16} className="mr-1" /> {t('tick_detail.back')}
        </Button>
        <span className="font-mono text-sm text-muted-foreground">{ticket.ticket_number}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <CardTitle className="text-lg">{ticket.subject}</CardTitle>
                <div className="flex gap-2">
                  <Badge variant="outline" className={priorityColors[ticket.priority]}>
                    {priorityLabel(ticket.priority)}
                  </Badge>
                  <Badge variant="outline" className={statusColors[ticket.status]}>
                    {statusLabel(ticket.status)}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-foreground whitespace-pre-wrap">{ticket.body || t('tick_detail.noBody')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('tick_detail.messages', { count: messages.length })}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {messages.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">{t('tick_detail.noMessages')}</p>
              )}
              {messages.map((msg) => {
                const isMe = msg.sender_id === user?.id;
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-4 py-2 ${isMe ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                      <p className="text-xs font-medium mb-1 opacity-75">
                        {ticketSenderLabel(msg)}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      {msg.has_attachment && msg.attachment_name && (
                        <TicketAttachment
                          ticketId={ticketId}
                          messageId={msg.id}
                          name={msg.attachment_name}
                          className={isMe ? 'text-primary-foreground' : ''}
                        />
                      )}
                      <p className="text-[10px] mt-1 opacity-50">{format(new Date(msg.created_at), 'dd.MM HH:mm')}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />

              {ticket.status !== 'closed' && (
                <div className="pt-2 border-t border-border space-y-2">
                  {selectedFile && (
                    <div className="flex items-center gap-2 text-xs bg-muted rounded px-2 py-1.5">
                      {selectedFile.type.startsWith('image/') ? <ImageIcon size={14} /> : <FileText size={14} />}
                      <span className="truncate flex-1">{selectedFile.name}</span>
                      <span className="text-muted-foreground">{(selectedFile.size / 1024 / 1024).toFixed(1)} MB</span>
                      <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                        <X size={14} className="text-muted-foreground hover:text-foreground" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                      onChange={handleFileSelect}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="self-end"
                      type="button"
                    >
                      <Paperclip size={14} />
                    </Button>
                    <Textarea
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      placeholder={t('tick_detail.messagePh')}
                      rows={2}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      disabled={(!message.trim() && !selectedFile) || sendMutation.isPending}
                      onClick={() => sendMutation.mutate()}
                      className="self-end"
                    >
                      <Send size={14} />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          {isOwnerOrManager && ticket.status !== 'closed' && (
            <Card>
              <CardHeader><CardTitle className="text-sm">{t('tick_detail.changeStatus')}</CardTitle></CardHeader>
              <CardContent>
                <Select value={ticket.status} onValueChange={(v) => statusMutation.mutate(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">{t('tick_common.status.new')}</SelectItem>
                    <SelectItem value="reviewing">{t('tick_common.status.reviewing')}</SelectItem>
                    <SelectItem value="resolved">{t('tick_common.status.resolved')}</SelectItem>
                    <SelectItem value="closed">{t('tick_common.status.closed')}</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-sm">{t('tick_detail.info')}</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">{t('tick_detail.creator')}</span>
                <p className="font-medium">{ticket.created_by_user?.name || '—'}</p>
                {ticket.created_by_user?.phone && <p className="text-xs text-muted-foreground">{ticket.created_by_user.phone}</p>}
              </div>
              <div>
                <span className="text-muted-foreground">{t('tick_detail.branch')}</span>
                <p className="font-medium">{ticket.branch?.name || '—'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">{t('tick_detail.createdAt')}</span>
                <p className="flex items-center gap-1"><Clock size={12} /> {format(new Date(ticket.created_at), 'dd.MM.yyyy HH:mm')}</p>
              </div>
              <div>
                <span className="text-muted-foreground">{t('tick_detail.updatedAt')}</span>
                <p className="text-xs">{ticket.updated_at ? format(new Date(ticket.updated_at), 'dd.MM.yyyy HH:mm') : '—'}</p>
              </div>
            </CardContent>
          </Card>

          {ticket.product && (
            <Card>
              <CardHeader><CardTitle className="text-sm">{t('tick_detail.linkedProduct')}</CardTitle></CardHeader>
              <CardContent className="text-sm">
                <p className="font-medium">{ticket.product.name}</p>
                <p className="text-xs text-muted-foreground">{t('tick_detail.sku', { sku: ticket.product.sku })}</p>
              </CardContent>
            </Card>
          )}

          {ticket.module_code && (
            <Card>
              <CardHeader><CardTitle className="text-sm">{BUILTIN_MODULES[ticket.module_code] ? '🏠' : '🔌'} {t('tick_detail.linkedModule')}</CardTitle></CardHeader>
              <CardContent className="text-sm">
                <p className="font-medium">{BUILTIN_MODULES[ticket.module_code] || ticket.module_code}</p>
              </CardContent>
            </Card>
          )}

          {ticket.customer && (
            <Card>
              <CardHeader><CardTitle className="text-sm">{t('tick_detail.linkedCustomer')}</CardTitle></CardHeader>
              <CardContent className="text-sm">
                <p className="font-medium">{ticket.customer.full_name}</p>
                <p className="text-xs text-muted-foreground">{ticket.customer.phone}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
